import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { PublicBureauConsent } from "@/lib/api";

export default function BureauConsentPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [pan4, setPan4] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    PublicBureauConsent.view(token).then(setD).catch(() => setErr("This consent link is invalid or has expired."));
  }, [token]);

  const grant = async () => {
    setBusy(true); setErr("");
    try { setDone(await PublicBureauConsent.grant(token, { pan_last4: pan4, accepted })); }
    catch (e) { const x = e?.response?.data?.detail; setErr(x?.message || (typeof x === "string" ? x : "Could not record consent")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="bureau-consent-page" className="min-h-screen bg-[#07142e] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2"><ShieldCheck size={12} /> Sahyog Finance · Credit Bureau Consent</div>
        {!d && !err && <div className="mt-6 text-white/60 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {err && <div data-testid="bureau-consent-error" className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">{err}</div>}
        {d && (
          <>
            <h1 className="mt-3 text-xl font-semibold">Consent to obtain your credit report</h1>
            <p className="text-sm text-white/60 mt-1">Applicant: <b className="text-white/90">{d.applicant_name}</b> · Purpose: {d.purpose.replaceAll("_", " ").toLowerCase()}</p>
            <div data-testid="bureau-consent-text" className="mt-4 rounded-xl border border-white/10 bg-black/25 p-4 text-sm leading-relaxed text-white/85">{d.consent_text}<div className="mt-2 text-[10px] text-white/40">Version {d.consent_text_version}</div></div>
            {done || d.status === "GRANTED" ? (
              <div data-testid="bureau-consent-granted" className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">Consent recorded on {new Date(done?.granted_at || d.granted_at).toLocaleString("en-IN")}. Thank you — you may close this page.</div>
            ) : d.status !== "PENDING" ? (
              <div className="mt-4 text-sm text-amber-200">This consent request is {d.status.toLowerCase()} and can no longer be accepted.</div>
            ) : (
              <div className="mt-4 space-y-3">
                <label className="block text-xs text-white/60">Confirm the last 4 characters of your PAN
                  <input data-testid="bureau-consent-pan4" value={pan4} onChange={(e) => setPan4(e.target.value.toUpperCase())} maxLength={4} className="mt-1 w-full rounded bg-white/5 border border-white/15 px-3 py-2 text-white tracking-widest" placeholder="e.g. 234F" />
                </label>
                <label className="flex items-start gap-2 text-sm text-white/80">
                  <input data-testid="bureau-consent-accept" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" />
                  I have read the consent text above and I explicitly authorise Sahyog Finance to obtain my credit information report for this eligibility assessment.
                </label>
                <button data-testid="bureau-consent-submit" disabled={busy || !accepted || pan4.length !== 4} onClick={grant} className="w-full rounded-full bg-[#D4AF37] text-[#0A1F44] py-2 text-sm font-bold disabled:opacity-40">{busy ? "Recording…" : "I give my consent"}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
